/**
 * WorkflowNodePalette - Draggable node palette sidebar
 * Users can drag nodes from here onto the canvas
 */

import { DragEvent, memo } from 'react';
import { useWorkflowPalette } from '../../../../stores/workflow';
import type { NodePaletteItem } from '../../../../stores/workflow/types';

const paletteItems: NodePaletteItem[] = [
  // Core nodes
  {
    type: 'start',
    label: 'Start',
    description: 'Entry point for the workflow',
    category: 'core',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z'
        />
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
      </svg>
    ),
    defaultData: {
      label: 'Start',
      type: 'start',
      isConfigured: true
    }
  },
  {
    type: 'end',
    label: 'End',
    description: 'Exit point for the workflow',
    category: 'core',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z' />
      </svg>
    ),
    defaultData: {
      label: 'End',
      type: 'end',
      isConfigured: true
    }
  },
  // Agent nodes
  {
    type: 'agent',
    label: 'Similarity Agent',
    description: 'Searches for similar content',
    category: 'agent',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
      </svg>
    ),
    defaultData: {
      label: 'Similarity Agent',
      type: 'agent',
      agentType: 'similarity',
      canHandoffTo: [],
      isConfigured: false
    }
  },
  {
    type: 'agent',
    label: 'Reference Agent',
    description: 'Extracts references and sources',
    category: 'agent',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
        />
      </svg>
    ),
    defaultData: {
      label: 'Reference Agent',
      type: 'agent',
      agentType: 'reference',
      canHandoffTo: [],
      isConfigured: false
    }
  },
  {
    type: 'agent',
    label: 'Structure Agent',
    description: 'Analyzes KG structure',
    category: 'agent',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7'
        />
      </svg>
    ),
    defaultData: {
      label: 'Structure Agent',
      type: 'agent',
      agentType: 'structure',
      canHandoffTo: [],
      isConfigured: false
    }
  },
  {
    type: 'agent',
    label: 'Custom Agent',
    description: 'Custom configurable agent',
    category: 'agent',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
        />
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
      </svg>
    ),
    defaultData: {
      label: 'Custom Agent',
      type: 'agent',
      agentType: 'custom',
      canHandoffTo: [],
      isConfigured: false
    }
  },
  // Tool node
  {
    type: 'tool',
    label: 'Tool',
    description: 'Invokes an external tool',
    category: 'tool',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
        />
      </svg>
    ),
    defaultData: {
      label: 'Tool',
      type: 'tool',
      toolType: '',
      toolId: '',
      isConfigured: false
    }
  },
  // Logic node
  {
    type: 'condition',
    label: 'Condition',
    description: 'Branches workflow based on rules',
    category: 'logic',
    icon: (
      <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth={2}
          d='M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
        />
      </svg>
    ),
    defaultData: {
      label: 'Condition',
      type: 'condition',
      rules: [],
      combineMode: 'and',
      isConfigured: false
    }
  }
];

const categoryLabels = {
  core: 'Core',
  agent: 'Agents',
  tool: 'Tools',
  logic: 'Logic'
};

const categoryColors = {
  core: 'bg-gray-100 dark:bg-dark-background-tertiary',
  agent: 'bg-blue-50 dark:bg-blue-900/20',
  tool: 'bg-indigo-50 dark:bg-indigo-900/20',
  logic: 'bg-amber-50 dark:bg-amber-900/20'
};

export const WorkflowNodePalette = memo(() => {
  const { isPaletteOpen, setDraggedNodeType } = useWorkflowPalette();

  const onDragStart = (event: DragEvent, item: NodePaletteItem) => {
    // Store the node data in the drag event
    event.dataTransfer.setData(
      'application/reactflow',
      JSON.stringify({
        type: item.type,
        data: item.defaultData
      })
    );
    event.dataTransfer.effectAllowed = 'move';
    setDraggedNodeType(item.type);
  };

  const onDragEnd = () => {
    setDraggedNodeType(null);
  };

  if (!isPaletteOpen) return null;

  // Group items by category
  const groupedItems = paletteItems.reduce(
    (acc, item) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    },
    {} as Record<string, NodePaletteItem[]>
  );

  return (
    <div className='h-full w-64 flex-shrink-0 overflow-y-auto border-r border-gray-200 bg-white dark:border-border-dark dark:bg-dark-background-secondary'>
      {/* Header */}
      <div className='sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-3 dark:border-border-dark dark:bg-dark-background-secondary'>
        <h3 className='text-sm font-semibold text-gray-900 dark:text-dark-text'>Node Palette</h3>
        <p className='text-xs text-gray-500 dark:text-dark-text-secondary'>Drag nodes onto the canvas</p>
      </div>

      {/* Node Categories */}
      <div className='p-3'>
        {Object.entries(groupedItems).map(([category, items]) => (
          <div key={category} className='mb-4'>
            <h4 className='mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-text-secondary'>
              {categoryLabels[category as keyof typeof categoryLabels]}
            </h4>
            <div className='space-y-2'>
              {items.map((item, index) => (
                <div
                  key={`${item.type}-${index}`}
                  draggable
                  onDragStart={(e) => onDragStart(e, item)}
                  onDragEnd={onDragEnd}
                  className={`
                    flex cursor-grab items-center gap-3 rounded-lg border border-gray-200 p-3
                    transition-all hover:border-primary hover:shadow-sm active:cursor-grabbing
                    dark:border-border-dark dark:hover:border-primary
                    ${categoryColors[item.category as keyof typeof categoryColors]}
                  `}
                >
                  <div className='text-gray-600 dark:text-dark-text-secondary'>{item.icon}</div>
                  <div className='flex-1 min-w-0'>
                    <div className='text-sm font-medium text-gray-900 dark:text-dark-text'>{item.label}</div>
                    <div className='truncate text-xs text-gray-500 dark:text-dark-text-secondary'>{item.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

WorkflowNodePalette.displayName = 'WorkflowNodePalette';
